import React, { useMemo } from "react";
import { Field, ErrorMessage } from "formik";
import Select from 'react-select';
import { useLifePolicyEntryList } from "../services/redux/api/components";

/**
 * PolicyDropdown
 * - safe handling if API returns unexpected shape
 * - ensures option children are primitive (string/number)
 * - supplies a name prop for Formik Field
 */
const PolicyDropdown = ({ name = "policyNumber", pageNumber = 1, pageSize = 10000, onSelect = null }) => {
  const { data: Policy, isLoading, isFetching, error } = useLifePolicyEntryList({
    pageNumber,
    pageSize,
  });

  // DEBUG: uncomment during development to inspect API shape
  // console.log("Policy raw:", Policy);

  // Normalize rows and ensure an id field exists
  const rowData = useMemo(() => {
    if (!Policy?.data) return [];
    // If API returns object with array at some key, try common keys
    if (Array.isArray(Policy.data)) return Policy.data;
    if (Array.isArray(Policy.data.rows)) return Policy.data.rows;
    if (Array.isArray(Policy.data.items)) return Policy.data.items;
    // If it's an object map, try to convert its values to array
    if (typeof Policy.data === "object") return Object.values(Policy.data);
    return [];
  }, [Policy]);

  const getPolicyDisplayName = (c) => {
    if (!c) return "";
    if (typeof c === "string" || typeof c === "number") return String(c);
    const policyNo = c.PolicyNumber ?? c.PolicyNo ?? c.PolicyId ?? c.PolicyID ?? c.id ?? '';
    if (c.LifeAssuredName) return policyNo ? `${policyNo} - ${String(c.LifeAssuredName)}` : String(c.LifeAssuredName);
    if (c.LifeAssured) return policyNo ? `${policyNo} - ${String(c.LifeAssured)}` : String(c.LifeAssured);
    if (c.ProductName) return policyNo ? `${policyNo} - ${String(c.ProductName)}` : String(c.ProductName);
    if (c.Name) return String(c.Name);
    if (c.DisplayName) return String(c.DisplayName);
    if (c.PolicyId) return String(c.PolicyId);
    if (c.id) return String(c.id);
    try {
      const s = JSON.stringify(c);
      return s.length > 60 ? s.slice(0, 60) + "..." : s;
    } catch {
      return "#";
    }
  };

  return (
    <div className="px-2 w-full">
      <label className="form-label">Policy</label>

      {/* Use Field render prop to integrate react-select with Formik and notify parent via onSelect */}
      <Field name={name}>
        {({ field, form }) => {
          const options = rowData.map((policy, index) => {
            const value = policy?.PolicyId ?? policy?.PolicyID ?? policy?.PolicyNumber ?? policy?.PolicyNo ?? policy?.id ?? index;
            const label = getPolicyDisplayName(policy);
            return { value: String(value), label, policy };
          });

          // Match field.value against multiple possible identifiers so pre-selected
          // options render correctly whether the form stores PolicyNumber or PolicyId
          const selectedOption = options.find(o => (
            String(o.value) === String(field.value) ||
            String(o.policy?.PolicyNumber) === String(field.value) ||
            String(o.policy?.PolicyNo) === String(field.value) ||
            String(o.policy?.PolicyId) === String(field.value) ||
            String(o.policy?.PolicyID) === String(field.value)
          )) || null;

          return (
            <div>
              <Select
                value={selectedOption}
                options={options}
                isLoading={isLoading || isFetching}
                onChange={(opt) => {
                  const newValue = opt ? opt.value : '';
                  form.setFieldValue(name, newValue);
                  const found = opt ? opt.policy : null;
                  if (onSelect && typeof onSelect === 'function') onSelect(found, { form, field });
                }}
                placeholder={isLoading || isFetching ? 'Loading Policy...' : 'Select Policy'}
                isClearable
                classNamePrefix="react-select"
                styles={{
                  container: (provided) => ({ ...provided, width: '100%' }),
                  control: (provided) => ({ ...provided, minHeight: 44 }),
                }}
              />
            </div>
          );
        }}
      </Field>

      <ErrorMessage name={name} component="div" className="text-red-600 text-sm mt-1" />
    </div>
  );
};

export default PolicyDropdown;
